import { RowData } from '../App';
import { Block } from '../classes/Block';
import type { VideoSettings, RenderFormat, VideoMode } from '../components/VideoSettings';
import { rebuildCardGroupsFromIndices } from '../utils/cardGroups';
import {
  DefaultCardSettings,
  parseDefaultCardSettings,
} from '../utils/defaultCardSettings';
import { parseCardFramingSettings } from '../utils/cardFraming';
import { FontSettingsMap } from '../utils/fontHelpers';

export interface DataColumn {
  field: string;
  headerName: string;
  flex?: number;
  sortable?: boolean;
  filter?: boolean;
  resizable?: boolean;
}

export type LoaderOnFileUpload = (
  data: RowData[],
  columns: DataColumn[],
  sheetName: string,
  blocks?: Block[],
  videoSettings?: VideoSettings,
  cardGroups?: import('../utils/cardGroups').CardGroup[],
  defaultCardSettings?: DefaultCardSettings
) => void;

export const DEFAULT_COLUMNS: DataColumn[] = [
  { field: 'col1', headerName: 'Label' },
  { field: 'col2', headerName: 'Value' }
];

export const getBooleanValue = (value: unknown, defaultValue: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
  if (typeof value === 'number') return value === 1;
  return defaultValue;
};

export const getNumericValue = (value: unknown, defaultValue: number): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && !isNaN(Number(value))) return Number(value);
  return defaultValue;
};

export type SbsBlocksOption = 'normal' | 'left' | 'right';

export const getSbsBlocksValue = (
  value: unknown,
  defaultValue: SbsBlocksOption
): SbsBlocksOption => {
  return Block.normalizeSbsBlocks(value, defaultValue);
};


export const parseVideoSettings = (firstRow: unknown[]): VideoSettings | undefined => {
  if (!Array.isArray(firstRow) || firstRow.length < 2 || firstRow[0] !== 'video_settings') {
    return undefined;
  }

  const settings: Record<string, unknown> = {};
  for (let i = 1; i < firstRow.length - 1; i += 2) {
    const key = firstRow[i];
    const value = firstRow[i + 1];
    if (key) {
      settings[String(key)] = value;
    }
  }

  if (Object.keys(settings).length === 0) {
    return undefined;
  }

  let fontsMap: FontSettingsMap | undefined;
  const fontsRaw = settings.fonts;
  if (typeof fontsRaw === 'string') {
    try {
      fontsMap = JSON.parse(fontsRaw);
    } catch {
      /* ignore */
    }
  } else if (fontsRaw && typeof fontsRaw === 'object') {
    fontsMap = fontsRaw as FontSettingsMap;
  }

  let logosMap: Record<string, { url: string; isEnabled: boolean }> | undefined;
  const logosRaw = (settings as any).logos;
  if (typeof logosRaw === 'string' && logosRaw.trim()) {
    try {
      const parsed = JSON.parse(logosRaw);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        logosMap = parsed;
      }
    } catch {
      /* ignore */
    }
  } else if (logosRaw && typeof logosRaw === 'object' && Object.keys(logosRaw).length > 0) {
    logosMap = logosRaw as Record<string, { url: string; isEnabled: boolean }>;
  }

  return {
    background_color: (settings.background_color as string) || '#000000',
    fps_num: parseInt(String(settings.fps_num)) || 30,
    resolution: (settings.resolution as string) || 'HD',
    transparent: getBooleanValue(settings.transparent, false),
    video_time: parseFloat(String(settings.video_time)) || 0,
    aspect_ratio: (settings.aspect_ratio as string) || '16:9',
    video_format: ((settings.video_format as string) || (settings.format as string) || 'H264') as RenderFormat,
    fonts: fontsMap || {},
    logos: logosMap || {},
    mode: (settings.mode as VideoMode) || "roller",
    card_framing: parseCardFramingSettings(settings.card_framing),
  };
};

export interface ExtractedData {
  allSheetDataRows: RowData[];
  blocks: Block[];
  videoSettings?: VideoSettings;
  columns: DataColumn[];
  cardGroups?: import('../utils/cardGroups').CardGroup[];
  defaultCardSettings?: DefaultCardSettings;
}

export const extractDataFromRows = (jsonData: unknown[]): ExtractedData => {
  if (!Array.isArray(jsonData) || jsonData.length === 0) {
    throw new Error('No data rows provided');
  }

  const columns: DataColumn[] = DEFAULT_COLUMNS;

  const allSheetDataRows: RowData[] = [];
  const allProcessedBlocks: Block[] = [];
  const blockCardIndices = new Map<number, number>();
  let currentBlock: Block | null = null;

  let videoSettings: VideoSettings | undefined;
  let defaultCardSettings: DefaultCardSettings | undefined;
  let dataStartIndex = 0;

  if (jsonData.length > 0) {
    videoSettings = parseVideoSettings(jsonData[0] as unknown[]);
    if (videoSettings) {
      dataStartIndex = 1;
      const firstRow: unknown[] = jsonData[0] as unknown[];
      const videoSettingsProps: Record<string, unknown> = {};
      for (let i = 1; i < firstRow.length - 1; i += 2) {
        const key: unknown = firstRow[i];
        if (!key) continue;
        videoSettingsProps[String(key)] = firstRow[i + 1];
      }
      defaultCardSettings = parseDefaultCardSettings(videoSettingsProps.default_card_settings);
    }
  }

  for (let i = dataStartIndex; i < jsonData.length; i++) {
    const rowArray = jsonData[i] as unknown[];
    const dataRow: RowData = {};
    rowArray.forEach((value, index) => {
      dataRow[`col${index}`] = value as string | number;
    });
    allSheetDataRows.push(dataRow);

    const blockName = String((rowArray[0] as unknown) || '').trim();
    if (blockName) {
      const props: Record<string, unknown> = {};
      if (rowArray.length > 2) {
        for (let j = 1; j < rowArray.length - 1; j += 2) {
          const key = String(rowArray[j]);
          const value = rowArray[j + 1];
          if (key) {
            props[key] = value;
          }
        }
      }

      const blockIndex: number = allProcessedBlocks.length;
      const cardIndexRaw = props['card_index'];
      if (cardIndexRaw !== undefined && cardIndexRaw !== null) {
        const cardIndex = getNumericValue(cardIndexRaw, -1);
        if (cardIndex >= 0) {
          blockCardIndices.set(blockIndex, cardIndex);
        }
      }

      const blockDefaults: typeof Block.defaults = Block.defaults;
      currentBlock = Block.create({
        name: blockName,
        shouldDisplayBlockName: getBooleanValue(props['show_block_name'], blockDefaults.shouldDisplayBlockName),
        blockNameFont: String(props['block_name_font_style'] || blockDefaults.blockNameFont),
        blockNameSize: getNumericValue(props['block_name_font_size'], blockDefaults.blockNameSize),
        blockNameWeight: getNumericValue(props['block_name_font_weight'], blockDefaults.blockNameWeight),
        blockNameFontWidth: getNumericValue(props['block_name_font_width'], blockDefaults.blockNameFontWidth),
        blockNameColor: String(props['block_name_color'] || blockDefaults.blockNameColor),
        blockNameAlignment: Block.normalizeBlockNameAlignment(props['block_name_alignment']),
        numColumns: getNumericValue(props['column_num'], blockDefaults.numColumns),
        labelValueGap: getNumericValue(props['label_value_gap'], blockDefaults.labelValueGap),
        distanceBelowBlockName: getNumericValue(props['distance_below_block_name'], blockDefaults.distanceBelowBlockName),
        marginColumn: getNumericValue(props['margin_column'], blockDefaults.marginColumn),
        edgeDistance: getNumericValue(props['edge_distance'], blockDefaults.edgeDistance),
        distanceBetweenRows: getNumericValue(props['distance_between_rows'], blockDefaults.distanceBetweenRows),
        distanceFromPreviousBlock: getNumericValue(props['distance_from_previous'], blockDefaults.distanceFromPreviousBlock),
        isSongBlock: getBooleanValue(props['song_block'], blockDefaults.isSongBlock),
        isSingleCell: getBooleanValue(props['single_cell'], blockDefaults.isSingleCell),
        isCreditsList: getBooleanValue(props['credits_list'], blockDefaults.isCreditsList),
        separator: String(props['credits_list_separator'] || blockDefaults.separator),
        isLogoBlock: getBooleanValue(props['logo_block'], blockDefaults.isLogoBlock),
        sbsBlocks: getSbsBlocksValue(props['sbs_blocks'], blockDefaults.sbsBlocks),
        sbsDistance: getNumericValue(props['sbs_distance'], blockDefaults.sbsDistance),
        labelFontName: String(props['label_font_name'] || blockDefaults.labelFontName),
        labelFontSize: getNumericValue(props['label_font_size'], blockDefaults.labelFontSize),
        labelFontWeight: getNumericValue(props['label_font_weight'], blockDefaults.labelFontWeight),
        labelFontWidth: getNumericValue(props['label_font_width'], blockDefaults.labelFontWidth),
        labelAlign: String(props['label_align'] || blockDefaults.labelAlign),
        labelVerticalAlign: String(props['label_vertical_align'] || blockDefaults.labelVerticalAlign),
        labelFontColor: String(props['label_color'] || blockDefaults.labelFontColor),
        valueFontName: String(props['value_font_name'] || blockDefaults.valueFontName),
        valueFontSize: getNumericValue(props['value_font_size'], blockDefaults.valueFontSize),
        valueFontWeight: getNumericValue(props['value_font_weight'], blockDefaults.valueFontWeight),
        valueFontWidth: getNumericValue(props['value_font_width'], blockDefaults.valueFontWidth),
        valueAlign: String(props['value_align'] || blockDefaults.valueAlign),
        valueVerticalAlign: String(props['value_vertical_align'] || blockDefaults.valueVerticalAlign),
        valueFontColor: String(props['value_color'] || blockDefaults.valueFontColor),
        hasSeparateValueFormatting: getBooleanValue(props['has_separate_value_formatting'], blockDefaults.hasSeparateValueFormatting),
        labelValueAlignment: Block.normalizeLabelValueAlignment(props['label_value_alignment']),
        cardFadeInFrames: getNumericValue(props['card_fade_in_frames'], blockDefaults.cardFadeInFrames),
        cardDurationFullyUpFrames: getNumericValue(props['card_duration_fully_up_frames'], blockDefaults.cardDurationFullyUpFrames),
        cardFadeOutFrames: getNumericValue(props['card_fade_out_frames'], blockDefaults.cardFadeOutFrames),
        cardSlugFrames: getNumericValue(props['card_slug_frames'], blockDefaults.cardSlugFrames),
        cardOverrideFullWidth: getBooleanValue(props['card_override_full_width'], blockDefaults.cardOverrideFullWidth),
        cardHorizontalOffsetPx: Block.normalizeCardHorizontalOffsetPx(props['card_horizontal_offset_px'], blockDefaults.cardHorizontalOffsetPx),
        exchangeLabelValue: getBooleanValue(props['exchange_label_value'], blockDefaults.exchangeLabelValue),
        isLocked: getBooleanValue(props['block_locked'], blockDefaults.isLocked),
        cardTimingLocked: getBooleanValue(props['card_timing_locked'], blockDefaults.cardTimingLocked),
        dataRows: []
      });
      allProcessedBlocks.push(currentBlock);
    } else if (currentBlock) {
      if (rowArray
        .slice(1)
        .some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
        currentBlock.dataRows.push(dataRow);
      }
      else {
        currentBlock.dataRows.push({ col1: '', col2: '' } as RowData);
      }
    }
  }

  for (const block of allProcessedBlocks) {
    if (block.dataRows.length === 0) {
      const emptyRow: RowData = {};
      for (const column of columns) {
        emptyRow[column.field] = '';
      }
      block.dataRows.push(emptyRow);
    }
  }

  const cardGroups =
    videoSettings?.mode === 'cards' && blockCardIndices.size > 0
      ? rebuildCardGroupsFromIndices(allProcessedBlocks, blockCardIndices)
      : undefined;

  return {
    allSheetDataRows,
    blocks: allProcessedBlocks,
    videoSettings,
    columns,
    cardGroups,
    defaultCardSettings,
  };
};
